import React, { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Inline "Translate to English" link.
 *
 * Renders as a tiny text button under a chat message / review body.
 * - Click → calls POST /api/translate and shows the English version below.
 * - Click "Show original" → hides the translation and shows the original.
 * - Detected language (e.g. "el", "es") shown as a small badge once known.
 *
 * Props
 * -----
 *   text       (string)  REQUIRED — the original text to translate
 *   sourceId   (string)  optional — message/review id for the testid suffix
 *   className  (string)  optional — extra classes on the outer wrapper
 */
const TranslateButton = ({ text, sourceId = '', className = '' }) => {
  const [translated, setTranslated] = useState(null);
  const [detected, setDetected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [error, setError] = useState(null);

  // Skip rendering entirely for trivial / empty content
  const trimmed = (text || '').trim();
  if (!trimmed || trimmed.length < 2) return null;

  const idSuffix = sourceId ? `-${sourceId}` : '';
  const token = localStorage.getItem('token');

  const handleTranslate = async () => {
    if (translated) {
      // Toggle visibility instead of re-fetching
      setShowOriginal((v) => !v);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await axios.post(
        `${API}/translate`,
        { text: trimmed, source_id: sourceId || undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTranslated(res.data.translated_text);
      setDetected(res.data.detected_language);
      setShowOriginal(false);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        'Translation unavailable. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // If translation already happened and the detected language is English,
  // hide the toggle entirely — nothing to translate.
  if (translated && detected === 'en' && translated === trimmed) {
    return null;
  }

  const buttonLabel = (() => {
    if (loading) return 'Translating…';
    if (!translated) return 'Translate';
    return showOriginal ? 'Show translation' : 'Show original';
  })();

  return (
    <div className={`mt-1 text-xs ${className}`} data-testid={`translate-block${idSuffix}`}>
      {translated && !showOriginal && (
        <div
          className="mb-1.5 px-2 py-1.5 rounded bg-white/5 border-l-2 border-emerald-400/60 text-gray-100 leading-relaxed"
          data-testid={`translated-text${idSuffix}`}
        >
          {translated}
          {detected && detected !== 'en' && (
            <span className="ml-2 inline-block text-[10px] uppercase tracking-wide text-emerald-300/70 align-middle">
              {detected} → en
            </span>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={handleTranslate}
        disabled={loading}
        className="inline-flex items-center gap-1 text-gray-400 hover:text-emerald-300 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid={`translate-btn${idSuffix}`}
      >
        {loading ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Languages className="h-3 w-3" />
        )}
        <span>{buttonLabel}</span>
      </button>
      {error && (
        <div
          className="mt-1 text-red-400 text-[11px]"
          data-testid={`translate-error${idSuffix}`}
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default TranslateButton;
